import React, { useEffect, useState } from "react";
import { TitleText } from "../../ui/Text";
import styled, { keyframes } from "styled-components";
import { NavLink, useHistory } from "react-router-dom";
import { GetHook } from "../../api/api";
import { SCORECARD_URL } from "../../api/urls";
import { get } from "../../utils/get";
import ObjectiveAccordion from "./ObjectiveAccordion";
import { PerpectiveSubject } from "../../subjects/PerspectiveSubject";
import moment from "moment";
import { StatusIcon } from "../../assets/svg/StatusIcon";
import { sortPerspectives } from "../../utils/sortPerspectivesScorecard";

interface AccordionProps {
  scorecardId?: string;
  path: any;
  lastLocationP: string | undefined;
  dateFilter: any;
}

const PerspectiveAccordion = ({ path, scorecardId,lastLocationP, dateFilter }: AccordionProps) => {
  const { location, push } = useHistory();
  const [isActive, setIsActive] = useState(false);
  const [score, setScore] = useState<any>();

  const lastSegment = location.pathname.split("/").pop();
  const date = `2024-01-01/2024-12-31`;

  let { data } = GetHook(
    scorecardId
      ? `${SCORECARD_URL}/${scorecardId}/perspective?interval=${date}`
      : "null"
  );

  useEffect(() => {
    const perspectiveData = PerpectiveSubject.subscribe((payload) => {
      setScore(payload);
    });
    return () => {
      perspectiveData.unsubscribe();
    };
  }, []);

  const getColor = (score) => {
    switch (true) {
      case score >= 1 && score < 2:
        return "#D51067";
      case score >= 2 && score <= 3.9:
        return "#EDBC0C";
      case score > 3:
        return "#4BD041";
      case score >= 5:
        return "url(#grad2)";
      default:
        return "#D51067";
    }
  };

  data = Array.isArray(data) && data.length >= 1 ? data : [];
  const sortedPerspectives = sortPerspectives(data);
return sortedPerspectives.map((perspective: any, index) => (
  <AccordionNavContainer key={index}>
    <PerspectiveItem
      onClick={() => {
        setIsActive(true);
        PerpectiveSubject.next(perspective);
      }}
      isActive={(_match, location) =>
        !!get(location, "pathname", "").includes(
          get(perspective, "perspective_id")
        )
      }
      to={
        get(location, "pathname", "").includes(
          get(perspective, "perspective_id")
        )
          ? {
              pathname: `${path}/${lastLocationP}`,
            }
          : {
              pathname: `${path}/${scorecardId}/perspective/${get(
                perspective,
                "perspective_id"
              )}/${lastLocationP}`,
            }
      }
    >
      <StatusIconWrapper>
        <StatusIcon
          color={
            perspective?.id === get(score, "[0].id")
              ? getColor(get(score, "[0].periodic_score"))
              : getColor(get(perspective, "periodic_score"))
          }
        />
      </StatusIconWrapper>
      <TitleText>{get(perspective, "perspective_name")}</TitleText>
    </PerspectiveItem>
    {isActive && (
      <AccordionItemsWrapper
        style={
          get(location, "pathname", "").includes(
            get(perspective, "perspective_id")
          )
            ? { maxHeight: "1000px", opacity: 1 }
            : { maxHeight: 0, opacity: 0 }
        }
      >
        <ObjectiveAccordion
          dateFilter={dateFilter}
          lastLocation={lastSegment}
          scorecardId={`${scorecardId}`}
          perspectiveId={get(perspective, "perspective_id", "")}
        />
      </AccordionItemsWrapper>
    )}
  </AccordionNavContainer>
));
};

export default PerspectiveAccordion;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideDown = keyframes`
  from { max-height: 0; opacity: 0; }
  to { max-height: 1000px; opacity: 1; }
`;

const PerspectiveItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 1.5em 1.4em;
  text-decoration: none;
  opacity: 0.7;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;

  &:hover {
    background-color: #f0f2ff;
    opacity: 0.9;
  }

  &.active {
    background-color: #ebefff;
    opacity: 1;
    border-left-color: #01A8CE;

    ${TitleText} {
      color: #01A8CE;
      font-weight: 600;
    }
  }
`;

const AccordionNavContainer = styled.div`
  display: grid;
  animation: ${fadeIn} 0.5s ease-out;
`;

const AccordionItemsWrapper = styled.div`
  display: grid;
  grid-template-rows: repeat(auto, 3.5em);
  overflow: hidden;
  transition: all 0.3s ease;
  margin-left: 0.7em;
  animation: ${slideDown} 0.3s ease-out;
`;

const StatusIconWrapper = styled.span`
  padding-right: 1em;
  display: flex;
  align-items: center;
`;
